<script setup lang="ts">
import {
  computed,
  defineAsyncComponent,
  nextTick,
  onBeforeUnmount,
  onMounted,
  reactive,
  ref,
  watch,
} from "vue";
import { useRoute, useRouter } from "vue-router";
import {
  Archive,
  Building2,
  CalendarRange,
  ChevronDown,
  ChevronRight,
  ClipboardPaste,
  Copy,
  Download,
  FileStack,
  FileText,
  Info,
  LockKeyhole,
  Plus,
  Save,
  Search,
  ShieldCheck,
  UsersRound,
} from "lucide-vue-next";
import PersonAvatar from "@/components/people/PersonAvatar.vue";
import { useWorkspaceStore } from "@/stores/workspace";
import ProjectScheduleMoveDialog from "@/components/views/ProjectScheduleMoveDialog.vue";
import type {
  DirectoryPerson,
  Participant,
  ProjectBrief,
  ProjectInvitation,
  ProjectRole,
  ProjectScheduleMovePreview,
  ProjectSummary,
  RichTextDocument,
} from "@/types/models";
import { emptyRichTextDocument, richTextOrPlain } from "@/utils/rich-text";
import { parseTaskImport, TASK_IMPORT_TEMPLATE } from "@/utils/task-import";
import {
  PROJECT_ROLE_DEFINITIONS,
  projectRoleDefinition,
  projectRoleOptions,
} from "@/utils/project-roles";
import type { TaskImportPreview } from "@/types/models";

const workspace = useWorkspaceStore();
const RichTextEditor = defineAsyncComponent(
  () => import("@/components/content/RichTextEditor.vue"),
);
const RichTextRenderer = defineAsyncComponent(
  () => import("@/components/content/RichTextRenderer.vue"),
);
const router = useRouter();
const route = useRoute();
const tab = ref(
  !workspace.isInternal
    ? "exports"
    : [
          "details",
          "brief",
          "people",
          "organizations",
          "access",
          "imports",
          "exports",
        ].includes(String(route.query.settings))
      ? String(route.query.settings)
      : "details",
);
const saving = ref(false);
const error = ref("");
const success = ref("");
const detailsDescriptionValid = ref(true);
const reviewingSchedule = ref(false);
const applyingSchedule = ref(false);
const schedulePreview = ref<ProjectScheduleMovePreview | null>(null);
const inviteOpen = ref(false);
const removeParticipantId = ref("");
const roleSavingId = ref("");
const roleDrafts = reactive<Record<string, ProjectRole>>({});
const closeOpen = ref(false);
const closeReason = ref("");
const directoryQuery = ref("");
const directoryResults = ref<DirectoryPerson[]>([]);
const directorySearching = ref(false);
const directoryListOpen = ref(false);
const directoryActiveIndex = ref(-1);
const directoryTotal = ref(0);
const directoryNextOffset = ref<number | null>(null);
const downloadingExportId = ref("");
const includeInternalWork = ref(false);
const exportFormat = ref<"doc" | "html" | "pdf" | "xls">("pdf");
const templateName = ref("");
const templateDescription = ref("");
const briefDraft = ref<ProjectBrief | null>(null);
const briefLoading = ref(false);
const briefConflict = ref(false);
const briefContentValid = ref(true);
const organizationDraft = ref("");
const importText = ref("");
const importInternal = ref(false);
const importPreview = ref<TaskImportPreview | null>(null);
const importPreviewing = ref(false);
const importApplying = ref(false);
const importTemplateCopied = ref(false);
const details = reactive<{
  name: string;
  description: string;
  descriptionRichText: RichTextDocument;
  startDate: string;
  targetEndDate: string;
  targetPolicy: ProjectSummary["targetPolicy"];
  reason: string;
  weekdaysOnly: boolean;
  moveReason: string;
}>({
  name: "",
  description: "",
  descriptionRichText: emptyRichTextDocument(),
  startDate: "",
  targetEndDate: "",
  targetPolicy: "Hard boundary" as ProjectSummary["targetPolicy"],
  reason: "",
  weekdaysOnly: true,
  moveReason: "",
});
const invitation = reactive<{
  email: string;
  displayName: string;
  organizationId: string;
  role: ProjectInvitation["role"];
}>({
  email: "",
  displayName: "",
  organizationId: "",
  role: "client_member",
});
const tabs = [
  { id: "details", label: "Project details", icon: CalendarRange },
  {
    id: "brief",
    label: "Internal project brief",
    icon: FileText,
    internalOnly: true,
  },
  { id: "people", label: "Participants", icon: UsersRound },
  { id: "organizations", label: "Organizations", icon: Building2 },
  { id: "access", label: "Access & visibility", icon: LockKeyhole },
  { id: "imports", label: "Imports", icon: ClipboardPaste },
  { id: "exports", label: "Exports & templates", icon: Download },
];
const visibleTabs = computed(() =>
  workspace.isInternal
    ? tabs
    : tabs
        .filter((item) => item.id === "exports")
        .map((item) => ({ ...item, label: "Reports" })),
);
let directorySearchTimer: number | undefined;
let directorySearchSequence = 0;
let loadedDetailsSourceKey = "";

const canManage = computed(() => workspace.canManageProject);
const canExport = computed(() => workspace.canExportProject);
const scheduleChanged = computed(() => {
  const project = workspace.active?.project;
  if (!project) return false;
  const currentlyWeekdaysOnly =
    project.workingWeekdays.length === 5 &&
    !project.workingWeekdays.includes(6) &&
    !project.workingWeekdays.includes(7);
  return (
    details.startDate !== project.startDate ||
    details.weekdaysOnly !== currentlyWeekdaysOnly
  );
});
const selectedProjectOrganization = computed(() =>
  workspace.organizations.find(
    (organization) => organization.id === organizationDraft.value,
  ),
);
const parsedImport = computed(() =>
  parseTaskImport(importText.value, workspace.active?.project.startDate || ""),
);
const taskImportTemplate = computed(() => {
  const exampleDate = workspace.active?.project.startDate || "2026-08-03";
  return TASK_IMPORT_TEMPLATE.replace("2026-08-03", exampleDate);
});

watch(
  () => workspace.active?.project,
  (project) => {
    if (!project) return;
    const sourceKey = JSON.stringify([
      project.id,
      project.name,
      project.description,
      project.descriptionRichText,
      project.startDate,
      project.targetEndDate,
      project.targetPolicy,
      project.workingWeekdays,
      project.clientOrganizationId,
    ]);
    if (sourceKey === loadedDetailsSourceKey) return;
    loadedDetailsSourceKey = sourceKey;
    Object.assign(details, {
      name: project.name,
      description: project.description,
      descriptionRichText: richTextOrPlain(
        project.descriptionRichText,
        project.description,
      ),
      startDate: project.startDate,
      targetEndDate: project.targetEndDate,
      targetPolicy: project.targetPolicy,
      reason: "",
      weekdaysOnly:
        project.workingWeekdays.length === 5 &&
        !project.workingWeekdays.includes(6) &&
        !project.workingWeekdays.includes(7),
      moveReason: "",
    });
    organizationDraft.value = project.clientOrganizationId || "";
    schedulePreview.value = null;
    templateName.value = `${project.name} standard`;
  },
  { immediate: true },
);

watch(
  tab,
  (next) => {
    error.value = "";
    success.value = "";
    if (next === "people") {
      void Promise.all([
        workspace.loadInvitations(),
        workspace.loadOrganizations(),
      ]).catch(showLoadError);
    }
    if (next === "brief") void loadBrief();
    if (next === "organizations")
      void workspace.loadOrganizations().catch(showLoadError);
    if (next === "exports") void workspace.loadExports().catch(showLoadError);
  },
  { immediate: true },
);

watch(
  () => route.query.settings,
  (setting) => {
    const next = String(setting || "");
    if (
      visibleTabs.value.some((item) => item.id === next) &&
      tab.value !== next
    )
      tab.value = next;
    if (next === "exports" && route.query.focus === "template")
      void focusTemplateCreator();
  },
  { immediate: true },
);

watch(
  [() => workspace.loading, () => route.query.focus, canManage],
  ([loading, focus, allowed]) => {
    if (!loading && allowed && focus === "template")
      void focusTemplateCreator();
  },
  { immediate: true, flush: "post" },
);

watch(directoryQuery, (query) => {
  window.clearTimeout(directorySearchTimer);
  const normalized = query.trim();
  const requestSequence = ++directorySearchSequence;
  if (normalized.length === 1) {
    directorySearching.value = false;
    directoryResults.value = [];
    directoryTotal.value = 0;
    directoryNextOffset.value = null;
    return;
  }
  directorySearching.value = true;
  directorySearchTimer = window.setTimeout(() => {
    void searchDirectory(normalized, requestSequence, 0);
  }, 250);
});

watch(inviteOpen, (open) => {
  if (!open) {
    directoryListOpen.value = false;
    return;
  }
  directoryListOpen.value = true;
  if (!directoryResults.value.length && !directorySearching.value) {
    const requestSequence = ++directorySearchSequence;
    directorySearching.value = true;
    void searchDirectory("", requestSequence, 0);
  }
});

watch(
  [importText, importInternal],
  () => {
    importPreview.value = null;
    error.value = "";
    success.value = "";
  },
  { flush: "sync" },
);

function refreshCurrentAccess() {
  void workspace.refreshProjectAccess().catch(() => undefined);
}

function refreshAccessWhenVisible() {
  if (document.visibilityState === "visible") refreshCurrentAccess();
}

onBeforeUnmount(() => {
  window.clearTimeout(directorySearchTimer);
  window.removeEventListener("focus", refreshCurrentAccess);
  document.removeEventListener("visibilitychange", refreshAccessWhenVisible);
});
onMounted(() => {
  window.addEventListener("focus", refreshCurrentAccess);
  document.addEventListener("visibilitychange", refreshAccessWhenVisible);
  refreshCurrentAccess();
  if (route.query.settings === "exports" && route.query.focus === "template")
    void focusTemplateCreator();
});

function showLoadError() {
  error.value =
    "This setting could not be loaded. Check your connection and try again.";
}

function formatBytes(value?: number) {
  if (!value) return "";
  return value < 1_024 ? `${value} B` : `${(value / 1_024).toFixed(1)} KB`;
}

async function saveDetails() {
  if (!detailsDescriptionValid.value) return;
  const scheduleDraft = {
    startDate: details.startDate,
    weekdaysOnly: details.weekdaysOnly,
    moveReason: details.moveReason,
  };
  const keepScheduleDraft = scheduleChanged.value;
  saving.value = true;
  error.value = "";
  success.value = "";
  try {
    await workspace.updateProjectDetails(details);
    if (keepScheduleDraft) {
      await nextTick();
      Object.assign(details, scheduleDraft);
    }
    success.value = "Project details saved.";
    details.reason = "";
  } catch (saveError) {
    error.value =
      saveError instanceof Error
        ? saveError.message
        : "Project details could not be saved.";
  } finally {
    saving.value = false;
  }
}

async function reviewScheduleMove() {
  if (
    !scheduleChanged.value ||
    !details.startDate ||
    details.moveReason.trim().length < 4
  )
    return;
  reviewingSchedule.value = true;
  error.value = "";
  success.value = "";
  try {
    schedulePreview.value = await workspace.previewProjectScheduleMove({
      startDate: details.startDate,
      weekdaysOnly: details.weekdaysOnly,
      reason: details.moveReason.trim(),
    });
  } catch (previewError) {
    error.value =
      previewError instanceof Error
        ? previewError.message
        : "The schedule move could not be previewed. The current schedule has not changed.";
  } finally {
    reviewingSchedule.value = false;
  }
}

async function applyProjectScheduleMove() {
  if (!schedulePreview.value) return;
  applyingSchedule.value = true;
  error.value = "";
  success.value = "";
  try {
    const movedCount = schedulePreview.value.changes.length;
    await workspace.applyProjectScheduleMove(schedulePreview.value);
    schedulePreview.value = null;
    details.moveReason = "";
    success.value = `Project schedule moved. ${movedCount} work item${movedCount === 1 ? "" : "s"} updated.`;
  } catch (applyError) {
    schedulePreview.value = null;
    error.value =
      applyError instanceof Error
        ? applyError.message
        : "The schedule move could not be applied. The current schedule has not changed.";
  } finally {
    applyingSchedule.value = false;
  }
}

async function uploadProjectImage(file: File) {
  const project = workspace.active?.project;
  if (!project) throw new Error("This project is no longer available.");
  return workspace.uploadPastedImage({
    projectId: project.id,
    parentType: "project",
    visibility: "Shared",
    file,
  });
}

async function searchDirectory(
  query: string,
  requestSequence: number,
  offset: number,
) {
  error.value = "";
  try {
    const page = await workspace.directoryUsers(query, offset);
    if (requestSequence === directorySearchSequence) {
      directoryResults.value = offset
        ? [...directoryResults.value, ...page.items]
        : page.items;
      directoryTotal.value = page.total;
      directoryNextOffset.value = page.nextOffset;
      if (!offset) directoryActiveIndex.value = page.items.length ? 0 : -1;
      directoryListOpen.value = true;
    }
  } catch (searchError) {
    if (requestSequence === directorySearchSequence) {
      directoryResults.value = [];
      directoryTotal.value = 0;
      directoryNextOffset.value = null;
      error.value =
        searchError instanceof Error
          ? searchError.message
          : "The directory search could not be completed.";
    }
  } finally {
    if (requestSequence === directorySearchSequence)
      directorySearching.value = false;
  }
}

function openDirectoryList() {
  directoryListOpen.value = true;
  if (!directoryResults.value.length && !directorySearching.value) {
    const requestSequence = ++directorySearchSequence;
    directorySearching.value = true;
    void searchDirectory(directoryQuery.value.trim(), requestSequence, 0);
  }
}

function handleDirectoryKeydown(event: KeyboardEvent) {
  if (event.key === "Escape") {
    directoryListOpen.value = false;
    return;
  }
  if (!["ArrowDown", "ArrowUp", "Enter"].includes(event.key)) return;
  event.preventDefault();
  if (!directoryListOpen.value) {
    openDirectoryList();
    return;
  }
  if (event.key === "ArrowDown")
    directoryActiveIndex.value = Math.min(
      directoryResults.value.length - 1,
      directoryActiveIndex.value + 1,
    );
  if (event.key === "ArrowUp")
    directoryActiveIndex.value = Math.max(0, directoryActiveIndex.value - 1);
  if (event.key === "Enter") {
    const person = directoryResults.value[directoryActiveIndex.value];
    if (person) void addDirectoryPerson(person);
  }
}

function loadMoreDirectoryPeople() {
  if (directoryNextOffset.value === null || directorySearching.value) return;
  const requestSequence = directorySearchSequence;
  directorySearching.value = true;
  void searchDirectory(
    directoryQuery.value.trim(),
    requestSequence,
    directoryNextOffset.value,
  );
}

function selectTab(next: string) {
  if (!visibleTabs.value.some((item) => item.id === next)) return;
  tab.value = next;
  void router.replace({
    query: { ...route.query, settings: next, focus: undefined },
  });
}

function cloneBrief(brief: ProjectBrief): ProjectBrief {
  return {
    ...brief,
    content: structuredClone(brief.content),
  };
}

async function loadBrief() {
  if (!workspace.active || !workspace.isInternal || briefLoading.value) return;
  briefLoading.value = true;
  error.value = "";
  try {
    briefDraft.value = cloneBrief(await workspace.loadProjectBrief());
    briefConflict.value = false;
  } catch (loadError) {
    error.value =
      loadError instanceof Error
        ? loadError.message
        : "The Internal project brief could not be loaded. Check your connection and try again.";
  } finally {
    briefLoading.value = false;
  }
}

async function saveBrief() {
  if (!briefDraft.value || !briefContentValid.value) return;
  saving.value = true;
  error.value = "";
  success.value = "";
  try {
    const saved = await workspace.saveProjectBrief(briefDraft.value);
    briefDraft.value = cloneBrief(saved);
    briefConflict.value = false;
    success.value = "Internal project brief saved.";
  } catch (saveError) {
    briefConflict.value =
      saveError instanceof Error && saveError.message.includes("changed since");
    error.value =
      saveError instanceof Error
        ? saveError.message
        : "The Internal project brief could not be saved. Your draft is still here. Check your connection and try again.";
  } finally {
    saving.value = false;
  }
}

async function saveProjectOrganization() {
  saving.value = true;
  error.value = "";
  success.value = "";
  try {
    await workspace.updateProjectOrganization(organizationDraft.value);
    success.value = organizationDraft.value
      ? "Client organization linked to this project."
      : "Client organization removed from this project.";
  } catch (saveError) {
    error.value =
      saveError instanceof Error
        ? saveError.message
        : "The client organization could not be changed. Your selection is still here.";
  } finally {
    saving.value = false;
  }
}

function selectMobileTab(event: Event) {
  const target = event.target;
  if (target instanceof HTMLSelectElement) selectTab(target.value);
}

async function focusTemplateCreator() {
  await nextTick();
  window.setTimeout(() => {
    const target = document.querySelector<HTMLElement>("#template-creator");
    target?.scrollIntoView({ block: "start" });
    target
      ?.querySelector<HTMLInputElement>("#template-name")
      ?.focus({ preventScroll: true });
  }, 0);
}

async function addDirectoryPerson(person: DirectoryPerson) {
  saving.value = true;
  error.value = "";
  try {
    await workspace.addParticipant(
      person,
      person.actorClass === "internal" ? "internal_member" : "client_member",
    );
    directoryResults.value = directoryResults.value.filter(
      (item) => item.id !== person.id,
    );
    directoryTotal.value = Math.max(0, directoryTotal.value - 1);
    success.value =
      person.platformState === "not_signed_in"
        ? `${person.displayName} invited. Their access will activate after Microsoft sign-in.`
        : `${person.displayName} added to this project.`;
  } catch (addError) {
    error.value =
      addError instanceof Error
        ? addError.message
        : "This person could not be added.";
  } finally {
    saving.value = false;
  }
}

async function sendInvitation() {
  if (!invitation.email.trim() || !invitation.displayName.trim()) return;
  saving.value = true;
  error.value = "";
  try {
    const created = await workspace.inviteParticipant({
      email: invitation.email.trim().toLowerCase(),
      displayName: invitation.displayName.trim(),
      ...(invitation.organizationId
        ? { organizationId: invitation.organizationId }
        : {}),
      role: invitation.role,
    });
    Object.assign(invitation, {
      email: "",
      displayName: "",
      organizationId: "",
      role: "client_member",
    });
    inviteOpen.value = false;
    success.value = ["emulator_queued", "queued", "accepted"].includes(
      created.deliveryState,
    )
      ? `Account setup email queued for ${created.displayName}.`
      : "Invitation created. Email delivery needs to be configured before the participant receives it.";
  } catch (inviteError) {
    error.value =
      inviteError instanceof Error
        ? inviteError.message
        : "The invitation could not be created.";
  } finally {
    saving.value = false;
  }
}

async function resendInvitation(id: string) {
  error.value = "";
  try {
    const updated = await workspace.resendInvitation(id);
    success.value =
      updated &&
      ["emulator_queued", "queued", "accepted"].includes(updated.deliveryState)
        ? "A new invitation email is queued. The previous link no longer works."
        : "Invitation renewed. Email delivery still needs to be configured.";
  } catch (resendError) {
    error.value =
      resendError instanceof Error
        ? resendError.message
        : "The invitation could not be resent.";
  }
}

async function revokeInvitation(id: string) {
  error.value = "";
  try {
    await workspace.revokeInvitation(id);
    success.value = "Invitation revoked.";
  } catch (revokeError) {
    error.value =
      revokeError instanceof Error
        ? revokeError.message
        : "The invitation could not be revoked.";
  }
}

async function removeParticipant(id: string) {
  saving.value = true;
  error.value = "";
  try {
    await workspace.removeParticipant(id);
    removeParticipantId.value = "";
    success.value = "Participant access removed.";
  } catch (removeError) {
    error.value =
      removeError instanceof Error
        ? removeError.message
        : "Participant access could not be removed.";
  } finally {
    saving.value = false;
  }
}

function participantRoleValue(person: Participant): ProjectRole {
  const roles: Record<string, ProjectRole> = {
    "Project owner": "owner",
    "Internal editor": "internal_editor",
    "Internal member": "internal_member",
    "Client lead": "client_lead",
    "Client member": "client_member",
    Observer: "observer",
  };
  return (
    roles[person.role] ||
    (person.kind === "internal" ? "internal_member" : "client_member")
  );
}

function roleDraft(person: Participant): ProjectRole {
  roleDrafts[person.id] ??= participantRoleValue(person);
  return roleDrafts[person.id]!;
}

function setRoleDraft(person: Participant, event: Event) {
  const target = event.target;
  if (target instanceof HTMLSelectElement)
    roleDrafts[person.id] = target.value as ProjectRole;
}

async function saveParticipantRole(person: Participant) {
  const role = roleDraft(person);
  if (role === participantRoleValue(person)) return;
  roleSavingId.value = person.id;
  error.value = "";
  success.value = "";
  try {
    const updated = await workspace.updateParticipantRole(person, role);
    roleDrafts[updated.id] = participantRoleValue(updated);
    success.value = `${updated.name}'s project role changed to ${updated.role}.`;
  } catch (roleError) {
    roleDrafts[person.id] = participantRoleValue(person);
    error.value =
      roleError instanceof Error
        ? roleError.message
        : "This project role could not be changed. Reload participants and try again.";
  } finally {
    roleSavingId.value = "";
  }
}

async function makeExport() {
  saving.value = true;
  error.value = "";
  success.value = "";
  try {
    const created = await workspace.createExport({
      audience: includeInternalWork.value ? "internal" : "shared",
      format: exportFormat.value,
    });
    if (!created) throw new Error("The export could not be created.");
    const fileName = await workspace.downloadExport(created.id);
    success.value = `${fileName} created and downloaded.`;
  } catch (exportError) {
    error.value =
      exportError instanceof Error
        ? exportError.message
        : "The export could not be created.";
  } finally {
    saving.value = false;
  }
}

function exportFormatLabel(format: "doc" | "html" | "pdf" | "xls") {
  if (format === "pdf") return "PDF report";
  if (format === "html") return "Printable HTML report";
  if (format === "doc") return "Word document";
  return "Excel workbook";
}

async function downloadJob(jobId: string) {
  downloadingExportId.value = jobId;
  error.value = "";
  success.value = "";
  try {
    const fileName = await workspace.downloadExport(jobId);
    success.value = `${fileName} downloaded.`;
  } catch (downloadError) {
    error.value =
      downloadError instanceof Error
        ? `${downloadError.message} Try creating a new export if this one expired.`
        : "The export could not be downloaded. Try creating it again.";
  } finally {
    downloadingExportId.value = "";
  }
}

async function makeTemplate() {
  if (!templateName.value.trim()) return;
  saving.value = true;
  error.value = "";
  try {
    const created = await workspace.createTemplate({
      name: templateName.value.trim(),
      description: templateDescription.value.trim(),
    });
    success.value = `Template created with ${created?.sectionCount ?? workspace.active?.sections.length ?? 0} sections and ${created?.taskCount ?? workspace.active?.tasks.length ?? 0} tasks. Owners and delivery progress were excluded.`;
  } catch (templateError) {
    error.value =
      templateError instanceof Error
        ? templateError.message
        : "The template could not be created.";
  } finally {
    saving.value = false;
  }
}

async function copyImportTemplate() {
  try {
    await navigator.clipboard.writeText(taskImportTemplate.value);
    importTemplateCopied.value = true;
    window.setTimeout(() => (importTemplateCopied.value = false), 2_000);
  } catch {
    error.value =
      "The template could not be copied. Select the sample and copy it manually.";
  }
}

async function reviewTaskImport() {
  if (!canManage.value || parsedImport.value.errors.length) return;
  importPreviewing.value = true;
  error.value = "";
  success.value = "";
  try {
    importPreview.value = await workspace.previewTaskImport(
      parsedImport.value.rows.map((row) => ({
        title: row.title,
        section: row.section,
        durationDays: row.durationDays,
        startDate: row.startDate,
      })),
      importInternal.value ? "Internal" : "Shared",
    );
  } catch (previewError) {
    error.value =
      previewError instanceof Error
        ? previewError.message
        : "The import could not be reviewed. Your pasted rows are still here.";
  } finally {
    importPreviewing.value = false;
  }
}

async function applyTaskImport() {
  if (!importPreview.value) return;
  importApplying.value = true;
  error.value = "";
  success.value = "";
  try {
    const count = importPreview.value.tasks.length;
    await workspace.applyTaskImport(importPreview.value);
    importText.value = "";
    importPreview.value = null;
    importInternal.value = false;
    success.value = `${count} task${count === 1 ? "" : "s"} imported. Review Unassigned work or use Bulk manage to finish the details.`;
  } catch (applyError) {
    error.value =
      applyError instanceof Error
        ? applyError.message
        : "The tasks were not imported. Your preview is still here; try again.";
  } finally {
    importApplying.value = false;
  }
}

async function confirmClose() {
  if (closeReason.value.trim().length < 4) return;
  saving.value = true;
  error.value = "";
  try {
    const archiving = workspace.active?.project.status === "Completed";
    if (archiving) await workspace.archiveProject(closeReason.value.trim());
    else await workspace.closeProject(closeReason.value.trim());
    closeOpen.value = false;
    success.value = archiving
      ? "Project archived. It has been removed from active work."
      : "Project closed. It is now read-only.";
    if (archiving) await router.push("/projects");
  } catch (closeError) {
    error.value =
      closeError instanceof Error
        ? closeError.message
        : "The project could not be closed.";
  } finally {
    saving.value = false;
  }
}
</script>

<template>
  <div class="settings-page">
    <aside>
      <h2>
        {{ workspace.isInternal ? "Project settings" : "Project reports" }}
      </h2>
      <div class="settings-mobile-picker">
        <label for="settings-section">Settings section</label>
        <select
          id="settings-section"
          class="select"
          :value="tab"
          @change="selectMobileTab"
        >
          <option v-for="item in visibleTabs" :key="item.id" :value="item.id">
            {{ item.label }}
          </option>
        </select>
      </div>
      <nav aria-label="Project settings sections">
        <button
          v-for="item in visibleTabs"
          :key="item.id"
          type="button"
          :class="{ active: tab === item.id }"
          @click="selectTab(item.id)"
        >
          <component :is="item.icon" :size="17" />{{ item.label
          }}<ChevronRight :size="15" />
        </button>
      </nav>
    </aside>
    <div class="settings-content">
      <p v-if="error" class="settings-alert settings-alert--error" role="alert">
        {{ error }}
      </p>
      <p
        v-if="success"
        class="settings-alert settings-alert--success"
        role="status"
      >
        {{ success }}
      </p>
      <section v-if="tab === 'details'" class="settings-section">
        <header>
          <h2>Project details</h2>
          <p>
            Update the client-facing identity, target, and schedule policy for
            this project.
          </p>
        </header>
        <p v-if="!canManage" class="permission-note">
          Only a project owner or workspace administrator can change project
          details and schedule settings. Your current values are read-only.
        </p>
        <div class="field">
          <label for="settings-name">Project name</label
          ><input
            id="settings-name"
            v-model="details.name"
            class="input"
            :disabled="!canManage"
          />
        </div>
        <div class="field">
          <span class="field-label">Description</span>
          <RichTextEditor
            v-model="details.descriptionRichText"
            label="Project description"
            :participants="workspace.active?.participants || []"
            :maxlength="2000"
            :disabled="!canManage"
            placeholder="Describe what this project is expected to deliver."
            :upload-pasted-image="uploadProjectImage"
            @update:plain-text="details.description = $event"
            @update:valid="detailsDescriptionValid = $event"
          />
        </div>
        <div class="field">
          <label for="settings-target">Project target</label
          ><input
            id="settings-target"
            v-model="details.targetEndDate"
            class="input"
            type="date"
            :disabled="!canManage"
          />
        </div>
        <div class="field">
          <label for="boundary-policy">Schedule boundary</label
          ><select
            id="boundary-policy"
            v-model="details.targetPolicy"
            class="select"
            :disabled="!canManage"
          >
            <option>Hard boundary</option>
            <option>Warn only</option></select
          ><span class="helper"
            >A hard boundary requires project-owner approval and a reason before
            work moves past the project target.</span
          >
        </div>
        <div
          v-if="
            details.targetEndDate !== workspace.active?.project.targetEndDate
          "
          class="field"
        >
          <label for="target-reason">Reason for changing the target</label>
          <textarea
            id="target-reason"
            v-model="details.reason"
            class="textarea"
            placeholder="Explain the schedule decision for the audit record."
            :disabled="!canManage"
          ></textarea>
        </div>
        <button
          class="button button--primary"
          type="button"
          :disabled="
            !canManage ||
            saving ||
            !detailsDescriptionValid ||
            !details.name.trim() ||
            (details.targetEndDate !==
              workspace.active?.project.targetEndDate &&
              details.reason.trim().length < 4)
          "
          @click="saveDetails"
        >
          <Save :size="16" /> {{ saving ? "Saving…" : "Save project details" }}
        </button>

        <section
          class="schedule-settings"
          aria-labelledby="schedule-settings-title"
        >
          <header>
            <div>
              <h3 id="schedule-settings-title">Move project schedule</h3>
              <p>
                Choose a new start or change whether weekends count as working
                days. The project target and scheduled work move together after
                you review the impact.
              </p>
            </div>
            <span class="schedule-version"
              >Schedule version
              {{ workspace.active?.project.scheduleVersion }}</span
            >
          </header>
          <div class="form-row schedule-fields">
            <div class="field">
              <label for="settings-start">New project start</label>
              <input
                id="settings-start"
                v-model="details.startDate"
                class="input"
                type="date"
                :disabled="!canManage || reviewingSchedule || applyingSchedule"
              />
            </div>
            <label class="workweek-option" for="weekdays-only">
              <input
                id="weekdays-only"
                v-model="details.weekdaysOnly"
                type="checkbox"
                :disabled="!canManage || reviewingSchedule || applyingSchedule"
              />
              <span>
                <strong>Weekdays only</strong>
                <small
                  >Schedule work Monday–Friday. Turn this off to allow weekend
                  work.</small
                >
              </span>
            </label>
          </div>
          <div v-if="scheduleChanged" class="field">
            <label for="schedule-move-reason"
              >Reason for moving the schedule</label
            >
            <textarea
              id="schedule-move-reason"
              v-model="details.moveReason"
              class="textarea"
              placeholder="Explain why the project schedule is moving."
              :disabled="!canManage || reviewingSchedule || applyingSchedule"
            ></textarea>
            <span class="helper"
              >This reason is recorded in the project audit history.</span
            >
          </div>
          <div class="schedule-actions">
            <button
              class="button button--secondary"
              type="button"
              :disabled="
                !canManage ||
                !scheduleChanged ||
                !details.startDate ||
                details.moveReason.trim().length < 4 ||
                reviewingSchedule ||
                applyingSchedule
              "
              @click="reviewScheduleMove"
            >
              <CalendarRange :size="16" />
              {{
                reviewingSchedule
                  ? "Preparing preview…"
                  : "Review schedule move"
              }}
            </button>
            <span v-if="!scheduleChanged" class="helper"
              >The project schedule has no pending changes.</span
            >
          </div>
        </section>
        <p v-if="!canManage" class="helper">
          Only the project owner or a workspace administrator can change these
          settings.
        </p>
      </section>
      <section v-else-if="tab === 'brief'" class="settings-section brief-page">
        <header class="section-with-action brief-header">
          <div>
            <span class="badge badge--internal">Internal</span>
            <h2>Project brief</h2>
            <p>
              Keep durable Internal project context here. Client participants
              cannot read this content.
            </p>
          </div>
          <button
            v-if="canManage"
            class="button button--primary"
            type="button"
            :disabled="
              saving || briefLoading || !briefDraft || !briefContentValid
            "
            @click="saveBrief"
          >
            <Save :size="16" /> {{ saving ? "Saving…" : "Save project brief" }}
          </button>
        </header>
        <p v-if="briefLoading" class="brief-state" role="status">
          Loading the Internal project brief…
        </p>
        <div v-else-if="briefDraft" class="brief-editor">
          <RichTextEditor
            v-if="canManage"
            v-model="briefDraft.content"
            profile="brief"
            label="Internal project brief"
            :maxlength="50000"
            placeholder="Add program scope, staffing, training, support hours, and other durable project context."
            @update:valid="briefContentValid = $event"
          />
          <RichTextRenderer v-else :document="briefDraft.content" />
        </div>
        <div v-if="briefConflict" class="brief-conflict" role="alert">
          <p>
            Your draft is still here. Copy any changes you need, then load the
            latest saved brief before editing again.
          </p>
          <button
            class="button button--secondary"
            type="button"
            @click="loadBrief"
          >
            Load latest brief
          </button>
        </div>
        <p v-if="briefDraft?.updatedAt" class="helper">
          Last saved by {{ briefDraft.updatedBy || "an Internal participant" }}.
        </p>
        <p v-if="!canManage && !briefLoading" class="helper">
          Only the project owner or a workspace administrator can change the
          Internal project brief.
        </p>
      </section>
      <section v-else-if="tab === 'people'" class="settings-section">
        <header class="section-with-action">
          <div>
            <h2>Participants</h2>
            <p>
              Project access is explicit. Organization membership alone does not
              grant access. Project owners can assign more than one Internal
              participant as a project owner.
            </p>
          </div>
          <button
            v-if="canManage"
            class="button button--primary"
            type="button"
            @click="inviteOpen = !inviteOpen"
          >
            <Plus :size="16" /> Invite participant
          </button>
        </header>
        <div v-if="inviteOpen" class="invite-panel">
          <div>
            <h3>Add a VIPdesk teammate</h3>
            <p>
              Choose someone from Corporate Team. New participants will sign in
              with Microsoft before their project access activates.
            </p>
          </div>
          <div class="directory-combobox">
            <label for="directory-query">VIPdesk teammate</label>
            <div class="search-field directory-combobox__control">
              <Search :size="16" aria-hidden="true" />
              <input
                id="directory-query"
                v-model="directoryQuery"
                role="combobox"
                autocomplete="off"
                aria-autocomplete="list"
                :aria-expanded="directoryListOpen"
                :aria-busy="directorySearching"
                aria-controls="directory-options"
                :aria-activedescendant="
                  directoryActiveIndex >= 0
                    ? `directory-option-${directoryActiveIndex}`
                    : undefined
                "
                placeholder="Search by name, title, or email"
                @focus="openDirectoryList"
                @click="openDirectoryList"
                @keydown="handleDirectoryKeydown"
              />
              <ChevronDown :size="16" aria-hidden="true" />
            </div>
            <span
              class="directory-search-state"
              role="status"
              aria-live="polite"
            >
              {{
                directoryQuery.trim().length === 1
                  ? "Type one more character to filter people"
                  : directorySearching
                    ? "Loading people…"
                    : `${directoryTotal} ${directoryTotal === 1 ? "person" : "people"} available`
              }}
            </span>
            <div
              v-if="directoryListOpen"
              id="directory-options"
              class="directory-options"
              role="listbox"
              aria-label="Available project participants"
            >
              <button
                v-for="(person, index) in directoryResults"
                :id="`directory-option-${index}`"
                :key="person.id"
                class="directory-option"
                :class="{
                  'directory-option--active': directoryActiveIndex === index,
                }"
                type="button"
                role="option"
                tabindex="-1"
                :aria-selected="directoryActiveIndex === index"
                :disabled="saving"
                @mouseenter="directoryActiveIndex = index"
                @click="addDirectoryPerson(person)"
              >
                <PersonAvatar
                  :name="person.displayName"
                  :initials="person.displayName.slice(0, 2).toUpperCase()"
                  :photo-url="person.photoDataUrl"
                />
                <span class="directory-option__identity">
                  <strong>{{ person.displayName }}</strong>
                  <small v-if="person.jobTitle">{{ person.jobTitle }}</small>
                  <small>{{ person.email }}</small>
                </span>
                <span class="directory-option__state">
                  <span
                    class="badge"
                    :class="
                      person.actorClass === 'internal'
                        ? 'badge--internal'
                        : 'badge--shared'
                    "
                  >
                    {{
                      person.actorClass === "internal" ? "VIPdesk" : "Client"
                    }}
                  </span>
                  <small v-if="person.platformState === 'not_signed_in'"
                    >New to the platform</small
                  >
                  <small v-else>Add to project</small>
                </span>
              </button>
              <div
                v-if="
                  !directorySearching &&
                  !directoryResults.length &&
                  directoryQuery.trim().length !== 1
                "
                class="directory-options__empty"
              >
                <strong>No people match this search.</strong>
                <span>Try a name, job title, or work email.</span>
              </div>
              <button
                v-if="directoryNextOffset !== null"
                class="directory-options__more"
                type="button"
                :disabled="directorySearching"
                @click="loadMoreDirectoryPeople"
              >
                {{ directorySearching ? "Loading…" : "Show more people" }}
              </button>
            </div>
          </div>
          <div class="invite-divider">
            <span>Invite a client participant</span>
          </div>
          <form class="invite-form" @submit.prevent="sendInvitation">
            <div class="form-row">
              <div class="field">
                <label for="invite-name">Name</label>
                <input
                  id="invite-name"
                  v-model="invitation.displayName"
                  class="input"
                  required
                />
              </div>
              <div class="field">
                <label for="invite-email">Email</label>
                <input
                  id="invite-email"
                  v-model="invitation.email"
                  class="input"
                  type="email"
                  required
                />
              </div>
            </div>
            <div class="form-row">
              <div class="field">
                <label for="invite-organization">Organization</label>
                <select
                  id="invite-organization"
                  v-model="invitation.organizationId"
                  class="select"
                >
                  <option value="">No organization</option>
                  <option
                    v-for="organization in workspace.organizations"
                    :key="organization.id"
                    :value="organization.id"
                  >
                    {{ organization.name }}
                  </option>
                </select>
              </div>
              <div class="field">
                <label for="invite-role">Project role</label>
                <select
                  id="invite-role"
                  v-model="invitation.role"
                  class="select"
                  aria-describedby="invite-role-description"
                >
                  <option value="client_lead">Client lead</option>
                  <option value="client_member">Client member</option>
                  <option value="observer">Observer</option>
                </select>
                <small id="invite-role-description" class="role-description">
                  {{ projectRoleDefinition(invitation.role).description }}
                </small>
              </div>
            </div>
            <p class="helper">
              A pending invitation grants no access. Access begins only after
              the intended email identity accepts it.
            </p>
            <button
              class="button button--primary"
              type="submit"
              :disabled="
                saving ||
                !invitation.email.trim() ||
                !invitation.displayName.trim()
              "
            >
              Send invitation
            </button>
          </form>
        </div>
        <div
          class="participant"
          v-for="person in workspace.active?.participants"
          :key="person.id"
        >
          <PersonAvatar
            :name="person.name"
            :initials="person.initials"
            :photo-url="person.photoUrl"
          />
          <span
            ><strong>{{ person.name }}</strong
            ><small v-if="person.jobTitle">{{ person.jobTitle }}</small
            ><small>{{ person.email }} · {{ person.organization }}</small></span
          ><span class="participant-role">
            <span class="participant-role__control">
              <select
                v-if="canManage"
                class="select select--compact"
                :value="roleDraft(person)"
                :aria-label="`Project role for ${person.name}`"
                :aria-describedby="`project-role-description-${person.id}`"
                :disabled="
                  person.id === workspace.user?.id ||
                  person.state === 'pending' ||
                  roleSavingId === person.id
                "
                @change="setRoleDraft(person, $event)"
              >
                <option
                  v-for="option in projectRoleOptions(person.kind)"
                  :key="option.value"
                  :value="option.value"
                >
                  {{ option.label }}
                </option>
              </select>
              <span
                v-else
                class="badge"
                :class="
                  person.kind === 'client' ? 'badge--shared' : 'badge--internal'
                "
                >{{ person.role }}</span
              >
              <span class="role-info">
                <button
                  class="role-info__button"
                  type="button"
                  :aria-label="`Access details for ${person.name}'s role`"
                  :aria-describedby="`project-role-description-${person.id}`"
                >
                  <Info :size="15" aria-hidden="true" />
                </button>
                <small
                  :id="`project-role-description-${person.id}`"
                  class="role-info__tooltip"
                  role="tooltip"
                >
                  {{ projectRoleDefinition(roleDraft(person)).description }}
                </small>
              </span>
            </span> </span
          ><span>{{
            person.state === "pending"
              ? "Waiting for Microsoft sign-in"
              : "Active"
          }}</span
          ><span class="participant-scope">
            <template v-if="!canManage || person.id === workspace.user?.id">
              {{
                person.id === workspace.user?.id
                  ? "Your role"
                  : "Explicit project access"
              }}
            </template>
            <span
              v-else-if="removeParticipantId === person.id"
              class="remove-confirm"
            >
              <button
                class="text-action"
                type="button"
                @click="removeParticipantId = ''"
              >
                Cancel
              </button>
              <button
                class="text-action text-action--danger"
                type="button"
                :disabled="saving"
                @click="removeParticipant(person.id)"
              >
                Confirm remove
              </button>
            </span>
            <span v-else class="participant-actions">
              <button
                v-if="roleDraft(person) !== participantRoleValue(person)"
                class="text-action"
                type="button"
                :disabled="roleSavingId === person.id"
                @click="saveParticipantRole(person)"
              >
                {{ roleSavingId === person.id ? "Saving…" : "Save role" }}
              </button>
              <button
                class="text-action text-action--danger"
                type="button"
                @click="removeParticipantId = person.id"
              >
                Remove
              </button>
            </span>
          </span>
        </div>
        <h3 v-if="workspace.invitations.length">Pending invitations</h3>
        <div
          v-for="pending in workspace.invitations"
          :key="pending.id"
          class="participant invitation-row"
        >
          <span class="avatar">{{
            pending.displayName.slice(0, 2).toUpperCase()
          }}</span>
          <span
            ><strong>{{ pending.displayName }}</strong
            ><small>{{ pending.email }}</small></span
          >
          <span class="badge badge--shared">{{
            pending.role.replaceAll("_", " ")
          }}</span>
          <span>{{
            pending.state === "pending" ? "Pending" : pending.state
          }}</span>
          <span
            v-if="pending.state === 'pending' && canManage"
            class="invitation-actions"
          >
            <button
              class="text-action"
              type="button"
              :disabled="saving"
              @click="resendInvitation(pending.id)"
            >
              Resend
            </button>
            <button
              class="text-action text-action--danger"
              type="button"
              :disabled="saving"
              @click="revokeInvitation(pending.id)"
            >
              Revoke
            </button>
          </span>
        </div>
      </section>
      <section v-else-if="tab === 'organizations'" class="settings-section">
        <header>
          <h2>Organizations</h2>
          <p>
            Link the client organization used for this project’s identity and
            branded exports. This never grants project access.
          </p>
        </header>
        <div class="project-organization">
          <span class="organization-mark organization-mark--large">
            <img
              v-if="selectedProjectOrganization?.logoDataUrl"
              :src="selectedProjectOrganization.logoDataUrl"
              :alt="`${selectedProjectOrganization.name} logo`"
            />
            <template v-else>{{
              selectedProjectOrganization?.name.slice(0, 2).toUpperCase() || "—"
            }}</template>
          </span>
          <div class="field">
            <label for="project-organization">Client organization</label>
            <select
              id="project-organization"
              v-model="organizationDraft"
              class="select"
              :disabled="!canManage || saving"
            >
              <option value="">No client organization</option>
              <option
                v-for="organization in workspace.organizations"
                :key="organization.id"
                :value="organization.id"
              >
                {{ organization.name }}
              </option>
            </select>
            <span class="helper">
              The stored logo appears in the project header and branded HTML or
              PDF exports.
            </span>
          </div>
          <button
            class="button button--primary"
            type="button"
            :disabled="
              !canManage ||
              saving ||
              organizationDraft ===
                (workspace.active?.project.clientOrganizationId || '')
            "
            @click="saveProjectOrganization"
          >
            <Save :size="16" />
            {{ saving ? "Saving…" : "Save client organization" }}
          </button>
        </div>
        <div class="organization-list">
          <span
            v-for="organization in workspace.organizations"
            :key="organization.id"
          >
            <span class="organization-mark">
              <img
                v-if="organization.logoDataUrl"
                :src="organization.logoDataUrl"
                :alt="`${organization.name} logo`"
              />
              <template v-else>{{
                organization.name.slice(0, 2).toUpperCase()
              }}</template>
            </span>
            <strong>{{ organization.name }}</strong>
            <small>{{ organization.website || "No website" }}</small>
          </span>
        </div>
        <button
          class="button button--secondary"
          type="button"
          @click="router.push('/organizations')"
        >
          Manage organizations
        </button>
      </section>
      <section
        v-else-if="tab === 'imports'"
        class="settings-section import-page"
      >
        <header>
          <h2>Import tasks</h2>
          <p>
            Copy rows from Excel and paste them here. Task is the only required
            column; blank values use Unassigned, one working day, and the
            project start.
          </p>
        </header>
        <div class="import-template" aria-labelledby="import-template-title">
          <div>
            <h3 id="import-template-title">Excel column template</h3>
            <p>Keep the columns in this order. Excel copies them with tabs.</p>
          </div>
          <button
            class="button button--secondary"
            type="button"
            @click="copyImportTemplate"
          >
            <Copy :size="15" />
            {{ importTemplateCopied ? "Template copied" : "Copy template" }}
          </button>
          <pre tabindex="0">{{ taskImportTemplate }}</pre>
        </div>
        <div class="field">
          <label for="task-import-paste">Paste tasks</label>
          <textarea
            id="task-import-paste"
            v-model="importText"
            class="textarea import-textarea"
            :disabled="!canManage || importApplying"
            spellcheck="false"
            placeholder="Task&#9;Section&#9;Duration&#9;Start date"
          ></textarea>
          <span class="helper"
            >Start date accepts YYYY-MM-DD or M/D/YYYY. Import up to 80 tasks at
            a time.</span
          >
        </div>
        <label class="internal-import-option">
          <input
            v-model="importInternal"
            type="checkbox"
            :disabled="!canManage || importApplying"
          />
          <span>
            <strong>Make imported tasks Internal</strong>
            <small
              >Only eligible VIPdesk project participants can see them.</small
            >
          </span>
        </label>
        <div
          v-if="importText && parsedImport.errors.length"
          class="import-errors"
          role="alert"
        >
          <strong>Fix these rows before reviewing the import:</strong>
          <ul>
            <li v-for="message in parsedImport.errors" :key="message">
              {{ message }}
            </li>
          </ul>
        </div>
        <div class="import-actions">
          <button
            class="button button--secondary"
            type="button"
            :disabled="
              !canManage ||
              !parsedImport.rows.length ||
              !!parsedImport.errors.length ||
              importPreviewing ||
              importApplying
            "
            @click="reviewTaskImport"
          >
            <ClipboardPaste :size="16" />
            {{ importPreviewing ? "Preparing preview…" : "Review import" }}
          </button>
          <span v-if="!canManage" class="helper"
            >Only a project owner or workspace administrator can import
            tasks.</span
          >
        </div>
        <section
          v-if="importPreview"
          class="import-preview"
          aria-labelledby="import-preview-title"
        >
          <header>
            <div>
              <h3 id="import-preview-title">
                {{ importPreview.tasks.length }} task{{
                  importPreview.tasks.length === 1 ? "" : "s"
                }}
                ready to import
              </h3>
              <p>
                {{ importPreview.newSections.length }} new section{{
                  importPreview.newSections.length === 1 ? "" : "s"
                }}
                will be created. Existing work is unchanged.
              </p>
            </div>
            <span
              class="badge"
              :class="
                importPreview.visibility === 'Internal'
                  ? 'badge--internal'
                  : 'badge--shared'
              "
              >{{ importPreview.visibility }}</span
            >
          </header>
          <div class="import-preview-table" role="region" tabindex="0">
            <table>
              <thead>
                <tr>
                  <th>Task</th>
                  <th>Section</th>
                  <th>Duration</th>
                  <th>Start</th>
                  <th>Finish</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="task in importPreview.tasks" :key="task.id">
                  <td>{{ task.title }}</td>
                  <td>{{ task.sectionName }}</td>
                  <td>
                    {{ task.durationDays }} day{{
                      task.durationDays === 1 ? "" : "s"
                    }}
                  </td>
                  <td>{{ task.startDate }}</td>
                  <td>{{ task.dueDate }}</td>
                </tr>
              </tbody>
            </table>
          </div>
          <footer>
            <button
              class="button button--secondary"
              type="button"
              :disabled="importApplying"
              @click="importPreview = null"
            >
              Edit pasted rows
            </button>
            <button
              class="button button--primary"
              type="button"
              :disabled="importApplying"
              @click="applyTaskImport"
            >
              {{ importApplying ? "Importing tasks…" : "Import tasks" }}
            </button>
          </footer>
        </section>
      </section>
      <section v-else-if="tab === 'access'" class="settings-section">
        <header>
          <h2>Access & visibility</h2>
          <p>
            Access is granted per project. Assignment does not grant planning or
            visibility administration.
          </p>
        </header>
        <div class="access-summary">
          <ShieldCheck :size="22" />
          <span>
            <strong
              >{{ workspace.active?.participants.length || 0 }} active
              participants</strong
            >
            <small
              >Internal content remains limited to eligible VIPdesk participants
              with an Internal role.</small
            >
          </span>
        </div>
        <ul class="access-rules role-reference">
          <li
            v-for="definition in PROJECT_ROLE_DEFINITIONS"
            :key="definition.value"
          >
            <strong>{{ definition.label }}</strong>
            <span>{{ definition.description }}</span>
          </li>
        </ul>
      </section>
      <section v-else class="settings-section">
        <header>
          <h2>
            {{ workspace.isInternal ? "Exports & templates" : "Reports" }}
          </h2>
          <p>
            Create a branded report or analysis-ready plan from the project data
            available to you. Reports include the overview, milestones,
            blockers, issues and risks, and the complete task plan. Download
            access is checked again and expires after 24 hours.
          </p>
        </header>
        <div v-if="canExport" class="export-builder">
          <div class="field">
            <label for="export-format">Format</label>
            <select id="export-format" v-model="exportFormat" class="select">
              <option value="pdf">PDF report</option>
              <option value="html">Printable HTML report</option>
              <option value="doc">Word document</option>
              <option value="xls">Excel workbook</option>
            </select>
          </div>
          <label
            v-if="workspace.isInternal"
            class="export-scope-option"
            for="include-internal-work"
          >
            <input
              id="include-internal-work"
              v-model="includeInternalWork"
              type="checkbox"
            />
            <span
              ><strong>Include Internal work</strong
              ><small
                >Creates an Internal report for VIPdesk participants.</small
              ></span
            >
          </label>
          <button
            class="button button--primary"
            type="button"
            :disabled="saving"
            @click="makeExport"
          >
            <Download :size="16" /> Create and download
          </button>
        </div>
        <p v-else class="permission-note">
          Your project role does not include report creation. Ask a project
          owner if you need a report from this project.
        </p>
        <div v-for="job in workspace.exports" :key="job.id" class="export-row">
          <PersonAvatar
            v-if="workspace.user"
            :name="workspace.user.displayName"
            :initials="workspace.user.initials"
            :photo-url="workspace.user.photoUrl"
          />
          <span class="export-row__details"
            ><strong>{{ exportFormatLabel(job.format) }}</strong
            ><small
              ><template v-if="job.audience === 'internal'"
                >Internal · </template
              ><template v-if="job.itemCount !== undefined">
                {{ job.itemCount }}
                {{ job.itemCount === 1 ? "task" : "tasks" }} ·
              </template>
              <template v-if="job.sizeBytes">
                {{ formatBytes(job.sizeBytes) }} ·
              </template>
              expires
              {{ new Date(job.expiresAt).toLocaleString("en-CA") }}</small
            ></span
          >
          <button
            class="button button--secondary"
            type="button"
            :disabled="job.state !== 'ready' || downloadingExportId === job.id"
            @click="downloadJob(job.id)"
          >
            {{
              downloadingExportId === job.id
                ? "Downloading…"
                : job.state === "ready"
                  ? "Download"
                  : job.state === "failed"
                    ? "Export failed"
                    : "Preparing…"
            }}
          </button>
        </div>
        <div
          v-if="canManage"
          id="template-creator"
          class="template-builder"
          tabindex="-1"
        >
          <header>
            <FileStack :size="20" />
            <span
              ><strong>Create template</strong
              ><small
                >Copies {{ workspace.active?.sections.length || 0 }} sections
                and {{ workspace.active?.tasks.length || 0 }} tasks, including
                hierarchy, relative timing, dependencies, forms, and checklists.
                Owners, history, files, comments, answers, risks, and progress
                are excluded.</small
              ></span
            >
          </header>
          <div class="form-row">
            <div class="field">
              <label for="template-name">Template name</label>
              <input id="template-name" v-model="templateName" class="input" />
            </div>
            <div class="field">
              <label for="template-description">Description</label>
              <input
                id="template-description"
                v-model="templateDescription"
                class="input"
              />
            </div>
          </div>
          <button
            class="button button--secondary"
            type="button"
            :disabled="saving || !templateName.trim()"
            @click="makeTemplate"
          >
            Create template
          </button>
        </div>
      </section>
      <section v-if="tab === 'details'" class="danger-zone">
        <div>
          <Archive :size="20" /><span
            ><strong>{{
              workspace.active?.project.status === "Completed"
                ? "Archive project"
                : "Close project"
            }}</strong
            ><small>{{
              workspace.active?.project.status === "Completed"
                ? "Archiving removes this completed project from active work. A workspace administrator can restore or delete it."
                : "Closed projects become read-only. You can archive the project after it is closed."
            }}</small></span
          >
        </div>
        <button
          v-if="!closeOpen"
          class="button button--secondary"
          type="button"
          :disabled="!canManage"
          @click="closeOpen = true"
        >
          {{
            workspace.active?.project.status === "Completed"
              ? "Review project archive"
              : "Review project closure"
          }}
        </button>
        <form v-else class="closure-form" @submit.prevent="confirmClose">
          <label for="closure-reason">{{
            workspace.active?.project.status === "Completed"
              ? "Reason for archiving"
              : "Reason for closing"
          }}</label>
          <input
            id="closure-reason"
            v-model="closeReason"
            class="input"
            :placeholder="
              workspace.active?.project.status === 'Completed'
                ? 'Record why this project is ready to archive'
                : 'Record the project decision'
            "
          />
          <button
            class="button button--secondary"
            type="button"
            @click="closeOpen = false"
          >
            Cancel
          </button>
          <button
            class="button button--danger"
            type="submit"
            :disabled="saving || closeReason.trim().length < 4"
          >
            {{
              workspace.active?.project.status === "Completed"
                ? "Archive project"
                : "Close project"
            }}
          </button>
        </form>
      </section>
    </div>
  </div>
  <ProjectScheduleMoveDialog
    :preview="schedulePreview"
    :applying="applyingSchedule"
    @close="schedulePreview = null"
    @apply="applyProjectScheduleMove"
  />
</template>

<style scoped>
.settings-page {
  height: 100%;
  overflow: auto;
  display: grid;
  grid-template-columns: 230px 1fr;
  background: var(--paper);
}
.settings-page > aside {
  position: sticky;
  top: 0;
  height: 100%;
  padding: 22px 14px;
  border-right: 1px solid var(--rule);
  background: var(--sheet-subtle);
}
.settings-page > aside h2 {
  padding: 0 8px 14px;
  font-size: 16px;
}
.settings-mobile-picker {
  display: none;
}
.settings-page nav {
  display: grid;
  gap: 3px;
}
.settings-page nav button {
  min-height: 41px;
  display: grid;
  grid-template-columns: 20px 1fr auto;
  align-items: center;
  gap: 8px;
  padding: 0 9px;
  border-radius: 5px;
  background: transparent;
  color: var(--text-soft);
  text-align: left;
  cursor: pointer;
}
.settings-page nav button:hover {
  background: var(--sheet-strong);
}
.settings-page nav button.active {
  background: var(--brand-soft);
  color: var(--brand-strong);
  font-weight: 700;
}
.settings-content {
  max-width: 900px;
  padding: 28px 32px 60px;
}
.settings-alert {
  margin-bottom: 14px;
  padding: 10px 12px;
  border: 1px solid;
  border-radius: 5px;
}
.settings-alert--error {
  border-color: var(--danger);
  background: var(--danger-soft);
  color: var(--danger);
}
.settings-alert--success {
  border-color: var(--success);
  background: var(--success-soft);
  color: var(--success);
}
.settings-section {
  display: grid;
  gap: 20px;
  padding: 24px;
  border: 1px solid var(--rule);
  border-radius: 7px;
  background: white;
}
.settings-section > header p {
  margin-top: 5px;
  color: var(--text-soft);
}
.settings-section > .button {
  justify-self: start;
}
.schedule-settings {
  display: grid;
  gap: 18px;
  margin-top: 4px;
  padding-top: 24px;
  border-top: 1px solid var(--rule-strong);
}
.schedule-settings > header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 20px;
}
.schedule-settings > header p {
  max-width: 66ch;
  margin-top: 4px;
  color: var(--text-soft);
}
.schedule-version {
  flex: 0 0 auto;
  padding: 4px 7px;
  border-radius: 4px;
  color: var(--text-soft);
  background: var(--sheet-subtle);
  font-size: 12px;
  font-weight: 700;
}
.schedule-fields {
  align-items: stretch;
}
.workweek-option {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  padding: 12px;
  border: 1px solid var(--rule);
  border-radius: 6px;
  background: var(--sheet-subtle);
  cursor: pointer;
}
.workweek-option input {
  width: 18px;
  height: 18px;
  margin-top: 1px;
  accent-color: var(--brand);
}
.workweek-option span {
  display: grid;
  gap: 3px;
}
.workweek-option small {
  color: var(--text-soft);
  line-height: 1.4;
}
.schedule-actions {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 12px;
}
.section-with-action {
  display: flex;
  justify-content: space-between;
  gap: 16px;
}
.brief-page {
  gap: 24px;
}
.brief-header {
  align-items: start;
}
.brief-header > div {
  display: grid;
  justify-items: start;
  gap: 7px;
}
.brief-header > div p {
  max-width: 70ch;
  color: var(--text-soft);
}
.brief-state {
  min-height: 160px;
  display: grid;
  place-items: center;
  color: var(--text-soft);
}
.brief-editor {
  min-width: 0;
}
.text-action:disabled {
  opacity: 0.42;
  cursor: not-allowed;
}
.brief-conflict {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  padding: 14px;
  border: 1px solid var(--warning);
  border-radius: var(--radius-sm);
  background: var(--warning-soft);
  color: var(--text);
}
.participant {
  min-height: 62px;
  display: grid;
  grid-template-columns: 32px minmax(150px, 1fr) minmax(220px, 280px) 70px auto;
  align-items: center;
  gap: 10px;
  padding: 8px 0;
  border-top: 1px solid var(--rule);
}
.participant > span:nth-child(2) {
  display: grid;
}
.participant small {
  color: var(--text-soft);
}
.participant-scope {
  color: var(--text-soft);
  font-size: 12px;
}
.participant-role .select {
  min-height: 34px;
}
.participant-role {
  min-width: 0;
}
.participant-role__control {
  display: flex;
  align-items: center;
  gap: 6px;
}
.role-description {
  display: block;
  color: var(--text-soft);
  font-size: 12px;
  line-height: 1.4;
  overflow-wrap: anywhere;
}
.role-info {
  position: relative;
  z-index: 1;
  display: inline-flex;
  flex: 0 0 auto;
}
.role-info:hover,
.role-info:focus-within {
  z-index: 10;
}
.role-info__button {
  width: 30px;
  height: 30px;
  display: inline-grid;
  place-items: center;
  border: 0;
  border-radius: var(--radius-sm);
  background: transparent;
  color: var(--text-soft);
  cursor: help;
}
.role-info__button:hover {
  background: var(--sheet-strong);
  color: var(--text);
}
.role-info__button:focus-visible {
  outline: 2px solid var(--focus);
  outline-offset: 2px;
}
.role-info__tooltip {
  position: absolute;
  z-index: 8;
  top: calc(100% + 6px);
  right: 0;
  width: min(300px, calc(100vw - 64px));
  padding: 9px 10px;
  border: 1px solid var(--rule-strong);
  border-radius: var(--radius-sm);
  background: var(--sheet);
  box-shadow: var(--shadow-menu);
  color: var(--text);
  font-size: 12px;
  line-height: 1.45;
  opacity: 0;
  pointer-events: none;
  transform: translateY(-2px);
  transition:
    opacity 120ms var(--ease),
    transform 120ms var(--ease);
}
.role-info:hover .role-info__tooltip,
.role-info:focus-within .role-info__tooltip {
  opacity: 1;
  transform: translateY(0);
}
.participant-actions {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 10px;
}
.text-action {
  background: transparent;
  color: var(--shared);
  font-size: 12px;
  font-weight: 700;
  cursor: pointer;
}
.text-action--danger {
  color: var(--danger);
}
.remove-confirm {
  display: flex;
  align-items: center;
  gap: 7px;
}
.invite-panel,
.template-builder {
  display: grid;
  gap: 16px;
  padding: 18px;
  border: 1px solid var(--rule);
  border-radius: 7px;
  background: var(--sheet-subtle);
}
.invite-panel > div:first-child p,
.template-builder small {
  color: var(--text-soft);
}
.directory-combobox {
  position: relative;
  display: grid;
  gap: 6px;
}
.directory-combobox > label {
  color: var(--text);
  font-size: 12px;
  font-weight: 700;
}
.search-field {
  min-height: 40px;
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 0 10px;
  border: 1px solid var(--rule);
  border-radius: 5px;
  background: var(--sheet);
}
.directory-combobox__control:focus-within {
  border-color: var(--focus);
  box-shadow: 0 0 0 3px color-mix(in srgb, var(--focus) 18%, transparent);
}
.search-field input {
  width: 100%;
  border: 0;
  outline: 0;
}
.directory-options {
  position: absolute;
  z-index: 20;
  top: calc(100% + 4px);
  left: 0;
  width: 100%;
  max-height: min(330px, 50vh);
  overflow-y: auto;
  display: grid;
  border: 1px solid var(--rule-strong);
  border-radius: var(--radius-sm);
  background: var(--sheet);
  box-shadow: var(--shadow-menu);
}
.directory-option {
  width: 100%;
  min-height: 68px;
  display: grid;
  grid-template-columns: 34px minmax(0, 1fr) auto;
  align-items: center;
  gap: 12px;
  padding: 10px 12px;
  border-bottom: 1px solid var(--rule);
  background: var(--sheet);
  color: var(--text);
  text-align: left;
  cursor: pointer;
}
.directory-option:last-of-type {
  border-bottom: 0;
}
.directory-option:hover,
.directory-option--active {
  background: var(--sheet-strong);
}
.directory-option:focus-visible {
  position: relative;
  z-index: 1;
  outline: 2px solid var(--focus);
  outline-offset: -2px;
}
.directory-option__identity,
.directory-option__state {
  display: grid;
  min-width: 0;
  gap: 2px;
}
.directory-option__identity strong,
.directory-option__identity small {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.directory-option__identity small,
.directory-option__state small,
.directory-options__empty {
  color: var(--text-soft);
}
.directory-option__state {
  justify-items: end;
}
.directory-options__empty {
  display: grid;
  gap: 3px;
  padding: 18px 12px;
  text-align: center;
}
.directory-options__empty strong {
  color: var(--text);
}
.directory-options__more {
  min-height: 44px;
  border-top: 1px solid var(--rule);
  background: var(--sheet-subtle);
  color: var(--shared);
  font-size: 12px;
  font-weight: 700;
  cursor: pointer;
}
.invite-divider {
  position: relative;
  display: grid;
  place-items: center;
  color: var(--text-soft);
  font-size: 11px;
}
.invite-divider::before {
  position: absolute;
  right: 0;
  left: 0;
  height: 1px;
  background: var(--rule);
  content: "";
}
.invite-divider span {
  position: relative;
  padding: 0 8px;
  background: var(--sheet-subtle);
}
.invite-form {
  display: grid;
  gap: 14px;
}
.invite-form > .button {
  justify-self: start;
}
.invitation-actions {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
}
.organization-list {
  display: grid;
  gap: 8px;
}
.organization-list > span {
  min-height: 52px;
  display: grid;
  grid-template-columns: 40px 1fr auto;
  align-items: center;
  gap: 8px;
  padding: 8px 10px;
  border: 1px solid var(--rule);
  border-radius: 5px;
}
.organization-list small {
  color: var(--text-soft);
}
.project-organization {
  display: grid;
  grid-template-columns: 64px minmax(220px, 1fr) auto;
  align-items: end;
  gap: 14px;
  padding: 16px;
  border: 1px solid var(--rule-strong);
  border-radius: var(--radius-sm);
  background: var(--sheet-subtle);
}
.organization-mark {
  width: 36px;
  height: 36px;
  overflow: hidden;
  display: grid;
  place-items: center;
  border: 1px solid var(--rule);
  border-radius: var(--radius-sm);
  background: var(--sheet);
  color: var(--ink-800);
  font-size: 11px;
  font-weight: 750;
}
.organization-mark--large {
  width: 64px;
  height: 52px;
}
.organization-mark img {
  width: 100%;
  height: 100%;
  display: block;
  padding: 4px;
  object-fit: contain;
}
.import-page {
  max-width: 980px;
}
.import-template {
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  gap: 12px 20px;
  padding: 16px;
  border: 1px solid var(--rule);
  border-radius: 6px;
  background: var(--sheet-subtle);
}
.import-template h3,
.import-template p {
  margin: 0;
}
.import-template p {
  margin-top: 3px;
  color: var(--text-soft);
}
.import-template pre {
  grid-column: 1 / -1;
  margin: 0;
  overflow-x: auto;
  padding: 12px;
  border: 1px solid var(--rule);
  border-radius: 5px;
  background: var(--sheet);
  color: var(--text);
  font:
    12px/1.55 "Segoe UI",
    system-ui,
    sans-serif;
  white-space: pre;
}
.import-textarea {
  min-height: 210px;
  resize: vertical;
  font-family: "Segoe UI", system-ui, sans-serif;
  font-variant-numeric: tabular-nums;
}
.internal-import-option {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  padding: 12px;
  border: 1px solid var(--rule);
  border-radius: 6px;
  background: var(--sheet);
}
.internal-import-option input {
  width: 17px;
  height: 17px;
  margin-top: 2px;
  accent-color: var(--brand-strong);
}
.internal-import-option span {
  display: grid;
  gap: 2px;
}
.internal-import-option small {
  color: var(--text-soft);
}
.import-errors {
  padding: 12px 14px;
  border: 1px solid #e5c7c7;
  border-radius: 6px;
  background: var(--danger-soft);
  color: var(--danger);
}
.import-errors ul {
  margin: 6px 0 0;
  padding-left: 20px;
}
.import-actions {
  display: flex;
  align-items: center;
  gap: 12px;
}
.import-preview {
  display: grid;
  gap: 14px;
  padding: 16px;
  border: 1px solid var(--rule-strong);
  border-radius: 7px;
  background: var(--sheet);
}
.import-preview > header,
.import-preview > footer {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
}
.import-preview h3,
.import-preview p {
  margin: 0;
}
.import-preview p {
  margin-top: 3px;
  color: var(--text-soft);
}
.import-preview > footer {
  justify-content: flex-end;
}
.import-preview-table {
  max-height: 360px;
  overflow: auto;
  border: 1px solid var(--rule);
}
.import-preview table {
  width: 100%;
  border-collapse: collapse;
  font-size: 13px;
}
.import-preview th,
.import-preview td {
  padding: 9px 10px;
  border-bottom: 1px solid var(--rule);
  text-align: left;
  white-space: nowrap;
}
.import-preview th {
  position: sticky;
  top: 0;
  z-index: 1;
  background: var(--sheet-strong);
  color: var(--text-soft);
  font-size: 11px;
  text-transform: uppercase;
}
.import-preview td:first-child {
  min-width: 240px;
  white-space: normal;
}
.access-summary {
  display: flex;
  gap: 12px;
  padding: 16px;
  border: 1px solid var(--success);
  border-radius: 6px;
  background: var(--success-soft);
  color: var(--success);
}
.access-summary span {
  display: grid;
  gap: 3px;
  color: var(--text);
}
.access-summary small {
  color: var(--text-soft);
}
.access-rules {
  display: grid;
  gap: 0;
  border: 1px solid var(--rule);
  border-radius: 6px;
  list-style: none;
}
.access-rules li {
  min-height: 54px;
  display: grid;
  grid-template-columns: 150px 1fr;
  align-items: center;
  gap: 12px;
  padding: 9px 12px;
}
.access-rules li + li {
  border-top: 1px solid var(--rule);
}
.access-rules span {
  color: var(--text-soft);
}
.permission-note {
  margin: 0;
  padding: 12px 14px;
  border: 1px solid var(--rule-strong);
  border-radius: 6px;
  background: var(--sheet-subtle);
  color: var(--text-soft);
}
.export-builder {
  display: grid;
  grid-template-columns: 1fr 1fr auto;
  align-items: end;
  gap: 12px;
  padding: 16px;
  border: 1px solid var(--rule);
  border-radius: 6px;
  background: var(--sheet-subtle);
}
.export-scope-option {
  min-height: 38px;
  display: flex;
  align-items: flex-start;
  gap: 8px;
  padding-bottom: 1px;
  cursor: pointer;
}
.export-scope-option input {
  width: 16px;
  height: 16px;
  margin-top: 2px;
  accent-color: var(--brand-strong);
}
.export-scope-option span,
.export-row__details {
  min-width: 0;
  display: grid;
}
.export-scope-option small {
  color: var(--text-soft);
}
.export-row {
  min-height: 58px;
  display: grid;
  grid-template-columns: 32px minmax(0, 1fr) auto;
  align-items: center;
  gap: 12px;
  padding: 8px 0;
  border-bottom: 1px solid var(--rule);
}
.export-row :deep(.avatar) {
  width: 30px;
  height: 30px;
  font-size: 10px;
}
.export-row small {
  color: var(--text-soft);
}
.template-builder {
  margin-top: 8px;
  background: white;
}
.template-builder > header {
  display: flex;
  gap: 9px;
}
.template-builder > header span {
  display: grid;
}
.template-builder > .button {
  justify-self: start;
}
.settings-empty {
  min-height: 240px;
  display: grid;
  place-content: center;
  justify-items: center;
  gap: 8px;
  padding: 22px;
  border: 1px dashed var(--rule-strong);
  border-radius: 7px;
  color: var(--text-soft);
  text-align: center;
}
.settings-empty strong {
  color: var(--text);
}
.settings-empty p {
  max-width: 500px;
}
.danger-zone {
  margin-top: 18px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 18px;
  padding: 18px 20px;
  border: 1px solid #e5c7c7;
  border-radius: 7px;
  background: #fffafa;
}
.danger-zone > div {
  display: flex;
  gap: 10px;
  color: var(--danger);
}
.danger-zone span {
  display: grid;
  color: var(--text);
}
.danger-zone small {
  color: var(--text-soft);
}
.closure-form {
  display: grid;
  grid-template-columns: minmax(160px, 1fr) auto auto;
  align-items: end;
  gap: 8px;
}
.closure-form label {
  grid-column: 1 / -1;
  color: var(--text);
  font-size: 12px;
  font-weight: 700;
}
@media (max-width: 750px) {
  .settings-page {
    grid-template-columns: 1fr;
  }
  .settings-page > aside {
    position: sticky;
    z-index: 3;
    height: auto;
    padding: 10px 14px;
    border-right: 0;
    border-bottom: 1px solid var(--rule);
  }
  .settings-page > aside h2 {
    display: none;
  }
  .settings-mobile-picker {
    display: grid;
    gap: 6px;
  }
  .settings-mobile-picker label {
    color: var(--text-soft);
    font-size: 12px;
    font-weight: 650;
  }
  .settings-page nav {
    display: none;
  }
  .settings-content {
    padding: 18px 14px 40px;
  }
  .settings-section {
    padding: 18px 14px;
  }
  .participant {
    grid-template-columns: 32px minmax(0, 1fr);
    align-items: start;
    gap: 8px 10px;
  }
  .participant > span:nth-child(3),
  .participant > span:nth-child(5) {
    grid-column: 2;
  }
  .participant > span:nth-child(4) {
    display: none;
  }
  .participant-actions {
    justify-content: flex-start;
  }
  .role-info__button {
    width: 44px;
    height: 44px;
  }
  .access-rules li {
    grid-template-columns: 1fr;
    align-items: start;
    gap: 4px;
  }
  .section-with-action,
  .danger-zone {
    align-items: stretch;
    flex-direction: column;
  }
  .brief-header .button,
  .brief-conflict .button {
    min-height: 44px;
  }
  .brief-header .button {
    width: 100%;
  }
  .brief-conflict {
    align-items: stretch;
    flex-direction: column;
  }
  .export-builder,
  .closure-form {
    grid-template-columns: 1fr;
  }
  .import-template {
    grid-template-columns: 1fr;
  }
  .import-template .button,
  .import-template pre {
    grid-column: 1;
  }
  .import-template .button {
    justify-self: start;
  }
  .import-actions,
  .import-preview > header,
  .import-preview > footer {
    align-items: stretch;
    flex-direction: column;
  }
  .import-preview > footer .button {
    min-height: 44px;
  }
  .export-row {
    grid-template-columns: 32px 1fr;
  }
  .directory-option {
    grid-template-columns: 34px minmax(0, 1fr);
    align-items: start;
  }
  .directory-option__state {
    grid-column: 2;
    justify-items: start;
  }
  .export-row > .button {
    grid-column: 2;
    justify-self: start;
  }
  .project-organization {
    grid-template-columns: 52px 1fr;
    align-items: start;
  }
  .project-organization .button {
    grid-column: 1 / -1;
    min-height: 44px;
  }
  .organization-mark--large {
    width: 52px;
    height: 46px;
  }
}
</style>
